"use client";
import { useMemo, useRef } from "react";
import StarIcon from "@mui/icons-material/Star";
import { Box, Skeleton, useMediaQuery } from "@mui/material";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import ActivityHeader from "./activityHeader";
import ActivityMobileHeader from "./activityMobileHeader";
import ReservationComponent from "./reservationComponent";
import StaticActivityInfo from "./staticInfo";
import GoogleTranslationComponent from "../googleTranslate/GoogleTranslationComponent";
import googleIcon from "@/assets/images/icons/googleIcon.svg";
import starIcon from "@/assets/images/icons/starIcon.svg";
import tripAdvisorIcon from "@/assets/images/icons/tripAdvisorIcon.svg";
import veeIcon from "@/assets/images/icons/veeIcon.svg";
import MyMapComponent from "@/common/customElements/mapWithMarker";

import { CircularDiv, CustomDiv, InlineDiv, Separator } from "@/common/divs";
import {
  AlmostBoldText,
  LightText,
  LightTextSpan,
  RegularText,
  SemiBoldText,
} from "@/common/texts";
import { useActivity } from "@/hooks/useActivity";
import { useFixHydration } from "@/hooks/useFixHydration";
import { ActivityDetailGeoSearch } from "@/model";
import { PRIMARY_BUTTON_COLOR } from "@/utils/constants";
import { renderMobilityTagIcon } from "@/utils/functions";
import "./activity.css";

const ActivityDetail = ({
  forceId,
  defaultActivity,
}: {
  forceId?: number;
  defaultActivity?: ActivityDetailGeoSearch;
}) => {
  //const { id: paramsId } = useParams<{ id: string }>();

  const id = useMemo(() => {
    if (forceId) {
      return forceId.toString();
    }
  }, [forceId]);

  const detail_info = useMemo(() => defaultActivity, [defaultActivity]);

  const { location, activity_comments, more_info, staticInfo } = useActivity(
    id ?? "",
    detail_info,
  );

  const { t } = useTranslation();

  const calendarRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery("(max-width: 600px)");

  const getImageForGoogle = () => {
    if (detail_info?.images) {
      return detail_info.images[0].link;
    } else {
      return "";
    }
  };
  const changeHeader = useMediaQuery("(max-width: 450px)");

  const mounted = useFixHydration();

  if (!mounted) {
    return null;
  }

  const showRatings = (): boolean => {
    if (detail_info) {
      if (detail_info.rating) {
        if (detail_info.rating > 0) {
          return true;
        }
      }
      if (detail_info.fh_google_rating) {
        if (detail_info.fh_google_rating > 0) {
          return true;
        }
      }
      if (detail_info.fh_tripadvisor_rating) {
        if (detail_info.fh_tripadvisor_rating > 0) {
          return true;
        }
      }
      return false;
    } else {
      return false;
    }
  };

  return (
    detail_info && staticInfo && more_info && activity_comments ?
      <>
        <img className="hidden" src={getImageForGoogle()} alt="Activity" />
        {changeHeader ?
          <ActivityMobileHeader activity={detail_info} />
        : <ActivityHeader activity={detail_info} />}

        <InlineDiv className="activity-container" paddingX="108px">
          <section className="main-section">
            {showRatings() ?
              <Box sx={{ marginTop: "20px" }}>
                <AlmostBoldText fontSize="24px">
                  {t("activity.opinions")}
                </AlmostBoldText>
                <InlineDiv
                  style={{
                    justifyContent: "space-between",
                    maxWidth: "300px",
                    marginTop: "15px",
                    marginLeft: "-10px",
                  }}
                >
                  {detail_info.fh_tripadvisor_rating ?
                    detail_info.fh_tripadvisor_rating > 0 ?
                      <InlineDiv
                        width="100px"
                        style={{
                          justifyContent: "space-evenly",
                          alignItems: "center",
                        }}
                      >
                        <Image
                          src={tripAdvisorIcon}
                          alt="tripAdvisor"
                          width={29}
                          height={29}
                        />
                        <Image
                          src={starIcon}
                          alt="star"
                          width={22}
                          height={22}
                          style={{ marginTop: "1px" }}
                        />
                        <LightText>
                          {detail_info.fh_tripadvisor_rating}
                        </LightText>
                      </InlineDiv>
                    : null
                  : null}
                  {detail_info.fh_google_rating ?
                    detail_info.fh_google_rating > 0 ?
                      <InlineDiv
                        width="100px"
                        style={{
                          justifyContent: "space-evenly",
                          alignItems: "center",
                        }}
                      >
                        <Image
                          src={googleIcon}
                          alt="google"
                          width={27}
                          height={27}
                          style={{
                            maxHeight: "27px",
                            maxWidth: "27px",
                            marginTop: "3px",
                          }}
                        />
                        <Image
                          src={starIcon}
                          alt="star"
                          width={22}
                          height={22}
                          style={{ marginTop: "1px" }}
                        />
                        <LightText>{detail_info.fh_google_rating}</LightText>
                      </InlineDiv>
                    : null
                  : null}
                  {detail_info.rating ?
                    detail_info.rating > 0 ?
                      <InlineDiv
                        width="100px"
                        style={{
                          justifyContent: "space-evenly",
                          alignItems: "center",
                        }}
                      >
                        <Image
                          src={veeIcon}
                          alt="tripAdvisor"
                          width={29}
                          height={29}
                          style={{ marginTop: "1px" }}
                        />
                        <Image
                          src={starIcon}
                          alt="star"
                          width={22}
                          height={22}
                        />
                        <LightText>{detail_info.rating}</LightText>
                      </InlineDiv>
                    : null
                  : null}
                </InlineDiv>
                <Separator
                  className="separator-activity-div"
                  style={{
                    marginTop: "27px",
                  }}
                />{" "}
              </Box>
            : null}
            {location && (
              <StaticActivityInfo
                location={location}
                info={staticInfo}
                more_info={more_info}
              />
            )}
            <Separator className="separator-activity-div" />
            <CustomDiv>
              <InlineDiv
                style={{ flexWrap: "wrap", justifyContent: "space-between" }}
              >
                <CustomDiv style={{ minWidth: "250px", width: "40%" }}>
                  <AlmostBoldText
                    fontSize="22px"
                    style={{ marginBottom: "16px", marginTop: "30px" }}
                  >
                    {t("activity.details.how_to_get")}
                  </AlmostBoldText>
                  <br />
                  <LightTextSpan
                    style={{
                      marginTop: "16px",
                      textAlign: "justify",
                    }}
                  >
                    {location?.formattedAddress}
                  </LightTextSpan>
                </CustomDiv>
                <CustomDiv>
                  {location?.latitude && location?.longitude && (
                    <MyMapComponent
                      zoom={15}
                      lat={parseFloat(location.latitude)}
                      lng={parseFloat(location.longitude)}
                    />
                  )}
                </CustomDiv>
              </InlineDiv>
              {location?.references &&
                location.references.map((ref, index) => (
                  <LightText
                    key={index}
                    style={{
                      marginTop: "16px",
                      textAlign: "justify",
                    }}
                  >
                    {ref.description}
                  </LightText>
                ))}
            </CustomDiv>
            <Separator
              className="separator-activity-div"
              style={{
                marginTop: "27px",
              }}
            />{" "}
            <CustomDiv style={{ marginBottom: "30px", marginTop: "30px" }}>
              <AlmostBoldText fontSize="22px">
                {t("activity.mobility")}
              </AlmostBoldText>
              <LightText style={{ marginTop: "16px", textAlign: "justify" }}>
                {t("activities.filter.mobility_recomendation")}:
              </LightText>
              <InlineDiv
                style={{
                  gap: "16px",
                  marginTop: "12px",
                  flexWrap: "wrap",
                }}
              >
                {detail_info?.mobilities?.map((option, index) => (
                  <a
                    key={index}
                    href={
                      "https://maps.google.com/?q=" +
                      location?.latitude +
                      "," +
                      location?.longitude
                    }
                    target="_blank"
                    rel="noreferrer"
                  >
                    <CircularDiv
                      background={PRIMARY_BUTTON_COLOR}
                      height="40px"
                      paddingX="16px"
                      width="fit-content"
                    >
                      <Image
                        src={renderMobilityTagIcon(option.id)}
                        alt={option.name}
                        width={24}
                        height={24}
                        style={{ width: "24px", height: "24px" }}
                      />
                      <RegularText fontWeight="300">
                        {" "}
                        <GoogleTranslationComponent
                          text={
                            (
                              option.name?.toLowerCase() == "coche" ||
                              option.name?.toLowerCase() == "car"
                            ) ?
                              "En coche"
                            : option.name
                          }
                        />
                      </RegularText>
                    </CircularDiv>
                  </a>
                ))}
              </InlineDiv>
            </CustomDiv>
            {detail_info.totalComments ?
              detail_info.totalComments > 0 && (
                <>
                  <Separator
                    className="separator-activity-div"
                    style={{
                      marginTop: "27px",
                    }}
                  />
                  <CustomDiv
                    style={{ marginBottom: "16px", marginTop: "30px" }}
                  >
                    <AlmostBoldText fontSize="22px">
                      {t("activity.opinions")}
                    </AlmostBoldText>

                    <InlineDiv
                      style={{ marginTop: "10px", alignItems: "center" }}
                    >
                      <SemiBoldText
                        fontSize="32px"
                        style={{ marginRight: "10px" }}
                      >
                        {detail_info.rating}
                      </SemiBoldText>
                      {Array.from({
                        length: detail_info.rating ?? 0,
                      }).map((_, index) => (
                        <StarIcon
                          key={index}
                          fontSize="small"
                          style={{
                            color: "#FFC107",
                            marginTop: "3px",
                          }}
                        />
                      ))}
                    </InlineDiv>

                    <InlineDiv style={{ gap: "20px", flexWrap: "wrap" }}>
                      {activity_comments ?
                        activity_comments?.content ?
                          activity_comments.content.map(
                            (comment, index) =>
                              comment.travelerComment != null && (
                                <CustomDiv
                                  key={index}
                                  width="265px"
                                  style={{ minWidth: "265px" }}
                                  height="260px"
                                  paddingY="16px"
                                  paddingX="24px"
                                  className="comments-div"
                                >
                                  <InlineDiv
                                    style={{
                                      marginLeft: "-5px",
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    {Array.from({
                                      length: comment.travelerGrade ?? 0,
                                    }).map((_, index) => (
                                      <StarIcon
                                        key={index}
                                        fontSize="small"
                                        style={{
                                          color: "#FFC107",
                                        }}
                                      />
                                    ))}
                                  </InlineDiv>
                                  <SemiBoldText
                                    fontSize="16px"
                                    style={{ marginTop: "12px" }}
                                  >
                                    {comment.travelerName}
                                  </SemiBoldText>
                                  <LightText
                                    fontSize="16px"
                                    style={{ marginTop: "8px" }}
                                  >
                                    {comment.travelerComment}
                                  </LightText>
                                </CustomDiv>
                              ),
                          )
                        : null
                      : null}
                    </InlineDiv>
                  </CustomDiv>
                </>
              )
            : null}
          </section>
          <section className="calendar-container">
            <ReservationComponent
              activityInfo={detail_info}
              location={location}
              calendarRef={calendarRef}
              activityId={id}
            />
          </section>
        </InlineDiv>
      </>
    : isMobile ?
      <CustomDiv paddingX="20px" marginY="10px">
        <Box>
          <Box sx={{ width: "100%" }}>
            <Skeleton variant="rounded" height={200}></Skeleton>
          </Box>
          <Box sx={{ width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-evenly",
                marginTop: "20px",
              }}
            >
              <Box sx={{ width: "45%" }}>
                <Skeleton variant="rounded" height={30}></Skeleton>
              </Box>
              <Box sx={{ width: "45%" }}>
                <Skeleton variant="rounded" height={30}></Skeleton>
              </Box>
            </Box>
            <Box sx={{ width: "100%", marginTop: "20px" }}>
              <Skeleton variant="rounded" height={30}></Skeleton>
            </Box>
            <Box sx={{ width: "35%", marginTop: "20px" }}>
              <Skeleton variant="rounded" height={30}></Skeleton>
            </Box>
          </Box>
        </Box>
        <Box sx={{ width: "60%", marginTop: "20px" }}>
          <Skeleton variant="rounded" height={30}></Skeleton>
        </Box>

        <Box sx={{ width: "100%", marginTop: "20px" }}>
          <Skeleton variant="rounded" height={300}></Skeleton>
        </Box>
      </CustomDiv>
    : <CustomDiv paddingX="108px" marginY="50px">
        <Box sx={{ display: "flex", gap: "16px" }}>
          <Box>
            <Skeleton variant="rounded" width={480} height={390}></Skeleton>
          </Box>
          <Box sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", gap: "16px" }}>
              <Box>
                <Skeleton variant="rounded" width={150} height={30}></Skeleton>
              </Box>
              <Box>
                <Skeleton variant="rounded" width={150} height={30}></Skeleton>
              </Box>
            </Box>
            <Box sx={{ width: "75%", marginTop: "30px" }}>
              <Skeleton variant="rounded" height={30}></Skeleton>
            </Box>
            <Box sx={{ width: "35%", marginTop: "30px" }}>
              <Skeleton variant="rounded" height={30}></Skeleton>
            </Box>
          </Box>
        </Box>
        <Box sx={{ width: "30%", marginTop: "30px" }}>
          <Skeleton variant="rounded" height={30}></Skeleton>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ width: "50%", marginTop: "30px" }}>
            <Skeleton variant="rounded" height={300}></Skeleton>
          </Box>
          <Box sx={{ width: "40%", marginTop: "-200px" }}>
            <Skeleton variant="rounded" height={500}></Skeleton>
          </Box>
        </Box>
      </CustomDiv>
  );
};

export default ActivityDetail;
